import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErpLink } from "../components/ErpLink";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { CompanyLogo } from "../components/CompanyLogo";
import { EmployeeAccountLink } from "../components/EmployeeAccountLink";
import { EmployeeSkills } from "../components/EmployeeSkills";
import { ManagerAssignment } from "../components/ManagerAssignment";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useDocList } from "../lib/api";

type Props = { company?: string };

type EmployeeRow = {
  name: string;
  employee_name: string;
  designation: string | null;
  department: string | null;
  company: string;
  status: string;
  user_id: string | null;
  reports_to: string | null;
  leave_approver: string | null;
  expense_approver: string | null;
  timesheet_approver: string | null;
  employee_advance_account: string | null;
};

const STATUS_PILL: Record<string, string> = {
  Active: "bg-green-50 text-status-green border-status-green/40",
  Left: "bg-brand-grey-light text-brand-charcoal border-brand-grey-light",
  Suspended: "bg-amber-50 text-status-amber border-status-amber/40",
};

// ERPNext stores the Department link as "Department name - ABBR"; drop the suffix for a cleaner display.
const shortDepartment = (d: string | null) => (d ? d.replace(/ - [A-Z0-9]{2,8}$/, "") : "–");

/** Searchable, clickable employee directory (People experience): every field comes straight from the Employee
 * DocType so it stays correct as employees join, move or leave — no separate summary to keep in sync. */
export default function EmployeeList({ company }: Props) {
  const { t } = useTranslation();
  const { hasRole, identity } = useAuth();
  const canLinkAccounts = hasRole("os_hr") || hasRole("os_admin");
  const [q, setQ] = useState("");
  const list = useDocList("Employee", {
    fields: ["name", "employee_name", "designation", "department", "company", "status", "user_id", "reports_to", "leave_approver", "expense_approver", "timesheet_approver", "employee_advance_account"],
    filters: company ? [["Employee", "company", "=", company]] : undefined,
    orderBy: "employee_name asc",
    page: 1,
    pageSize: 300,
  });
  const rows = (list.data ?? []) as unknown as EmployeeRow[];
  const myEmployee = useMemo(() => rows.find((r) => identity?.email && r.user_id === identity.email)?.name, [rows, identity]);
  const isManager = !!myEmployee && rows.some((r) => r.reports_to === myEmployee);
  const canManage = canLinkAccounts || isManager;
  const employeeName = useMemo(() => {
    const byId = new Map(rows.map((r) => [r.name, r.employee_name]));
    return (id: string | null) => (id ? byId.get(id) ?? id : null);
  }, [rows]);
  const approverName = useMemo(() => {
    const byUser = new Map(rows.filter((r) => r.user_id).map((r) => [r.user_id as string, r.employee_name]));
    return (userId: string | null) => (userId ? byUser.get(userId) ?? userId : null);
  }, [rows]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => [r.employee_name, r.designation, r.department, r.company].some((v) => (v ?? "").toLowerCase().includes(needle)));
  }, [rows, q]);

  return (
    <div className="mt-4 space-y-3" data-employee-list>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-brand-grey">{t("ppl.list.count", { n: filtered.length })}</div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ppl.list.searchPlaceholder")}
            className="w-72 rounded-md border border-brand-grey-light bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none"
            data-employee-search />
          {canLinkAccounts && (
            <Link to="/people/directory/new" data-employee-add
              className="rounded-md bg-brand-primary px-3 py-2 text-xs font-semibold text-white hover:bg-brand-deep">
              + {t("emp.form.addTitle")}
            </Link>
          )}
        </div>
      </div>

      {list.isError && <ErrorBox error={list.error} onRetry={() => void list.refetch()} />}

      <Card className="overflow-hidden">
        {list.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-brand-grey">{t("ppl.list.noResults")}</div>
        ) : (
          <div className="max-h-[36rem] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("ppl.list.name")}</th>
                  <th className="px-4 py-2">{t("ppl.list.designation")}</th>
                  <th className="px-4 py-2">{t("ppl.list.department")}</th>
                  <th className="px-4 py-2">{t("exec.division")}</th>
                  <th className="px-4 py-2">{t("ops.status")}</th>
                  {canManage && <th className="px-4 py-2">{t("ppl.manager.manager")}</th>}
                  {canManage && <th className="px-4 py-2">{t("ppl.skills.panelTitle")}</th>}
                  {canLinkAccounts && <th className="px-4 py-2">{t("ppl.list.account")}</th>}
                  {canLinkAccounts && <th className="px-4 py-2 text-right">{t("emp.form.edit")}</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.name} className="border-t border-brand-grey-light hover:bg-[#F6F8FA]" data-employee-row={r.name}>
                    <td className="px-4 py-2">
                      <ErpLink>
                        {r.employee_name}
                      </ErpLink>
                    </td>
                    <td className="px-4 py-2">{r.designation ?? "–"}</td>
                    <td className="px-4 py-2">{shortDepartment(r.department)}</td>
                    <td className="px-4 py-2"><CompanyLogo company={r.company} size={14} showName className="text-xs" /></td>
                    <td className="px-4 py-2"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[r.status] ?? STATUS_PILL.Left}`}>{t(`ppl.list.empStatus.${r.status}`, r.status)}</span></td>
                    {canManage && (
                      <td className="px-4 py-2">
                        <ManagerAssignment employee={r.name} reportsTo={r.reports_to} leaveApprover={r.leave_approver} expenseApprover={r.expense_approver}
                          timesheetApprover={r.timesheet_approver} advanceAccount={r.employee_advance_account} employeeName={employeeName} approverName={approverName} />
                      </td>
                    )}
                    {canManage && (
                      <td className="px-4 py-2">
                        <EmployeeSkills employee={r.name} />
                      </td>
                    )}
                    {canLinkAccounts && (
                      <td className="px-4 py-2">
                        <EmployeeAccountLink employee={r.name} employeeName={r.employee_name} userId={r.user_id} />
                      </td>
                    )}
                    {canLinkAccounts && (
                      <td className="px-4 py-2 text-right">
                        <Link to={`/people/directory/${encodeURIComponent(r.name)}/edit`} data-employee-edit={r.name}
                          className="text-xs font-semibold text-brand-primary hover:underline">{t("emp.form.edit")}</Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
